import { inject, Injectable, InjectionToken } from '@angular/core';

const DRAFT_KEY_PREFIX = 'dv4_draft';
const LAST_VIEW_KEY_PREFIX = 'dv4_last_view';

export const DATA_VIEW_STORAGE_BACKEND = new InjectionToken<Storage | null>('DATA_VIEW_STORAGE_BACKEND', {
  providedIn: 'root',
  factory: () => ignoringStorageErrors(() => globalThis.sessionStorage) ?? null,
});

@Injectable({ providedIn: 'root' })
export class DataViewDraftStorage {
  private readonly backend = inject(DATA_VIEW_STORAGE_BACKEND);

  readDraft<TState>(pageName: string, viewId: string): TState | undefined {
    const key = draftKey(pageName, viewId);
    const raw = this.read(key);
    if (raw === undefined) return undefined;

    try {
      return JSON.parse(raw) as TState;
    } catch {
      this.remove(key);
      return undefined;
    }
  }

  writeDraft<TState>(pageName: string, viewId: string, state: TState): void {
    this.write(draftKey(pageName, viewId), JSON.stringify(state));
  }

  clearDraft(pageName: string, viewId: string): void {
    this.remove(draftKey(pageName, viewId));
  }

  readLastViewId(pageName: string): string | undefined {
    return this.read(lastViewKey(pageName));
  }

  writeLastViewId(pageName: string, viewId: string): void {
    this.write(lastViewKey(pageName), viewId);
  }

  private read(key: string): string | undefined {
    return ignoringStorageErrors(() => this.backend?.getItem(key)) ?? undefined;
  }

  private write(key: string, value: string): void {
    ignoringStorageErrors(() => this.backend?.setItem(key, value));
  }

  private remove(key: string): void {
    ignoringStorageErrors(() => this.backend?.removeItem(key));
  }
}

function ignoringStorageErrors<T>(access: () => T): T | undefined {
  try {
    return access();
  } catch {
    return undefined;
  }
}

function draftKey(pageName: string, viewId: string): string {
  return `${DRAFT_KEY_PREFIX}_${pageName}_${viewId}`;
}

function lastViewKey(pageName: string): string {
  return `${LAST_VIEW_KEY_PREFIX}_${pageName}`;
}
